import React, { useEffect, useState } from 'react';
import classes from '../../../styles/AllDevices.module.css';
import myClasses from '../SmartCharging.module.css';
import Typography from '../../../components/Typography/Typography';
import { SCService } from '../../../services/SCService';
import { useLoader } from '../../../hooks';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { findDifferenceInHoursAndMinutesFormatted, momentTimeFormater } from '../../../utils/dateHelper';
import Table from '../../../components/Table/Table';
import { UilNotes } from '@iconscout/react-unicons';
import buttonClasses from '../../../components/Buttons/Button.module.css';
import CustomTooltip from '../../../components/CustomToolTip/CustomTooltip';
import ModalComponent from '../../../components/ModalComponent/ModalComponent';
import EventsTrasks from './ModalComponent/EventsTrasks';
import SODClasses from './SOD.module.css';
import { CamelCaseToTextCase } from '../../../utils/stringHelper';
import moment from 'moment';

const LIMIT = 15;

const LoadManagement = ({ selectedGroup = {}, refresh, date, selectedMode }) => {
    const [events, setEvents] = useState([]);
    const [startLoader, stopLoader] = useLoader();
    const [skip, setSkip] = useState(0);
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [infoModal, setInfoModal] = useState({ status: false, data: [] });
    const [overview, setOverview] = useState({
        sitePowerLimit: 0,
        currentDraw: 0,
        activeSessions: 0,
        lastRebalance: null,
    });

    useEffect(() => {
        if (selectedGroup.value) {
            fetchSiteOverview();
        }
    }, [selectedGroup?.value, refresh]);

    useEffect(() => {
        if (selectedGroup.value && date && selectedMode.value) {
            const params = {
                mode: selectedMode.value,
                groupId: selectedGroup.value,
                startDate: momentTimeFormater(date).startOf('day').utc().format(),
                endDate: momentTimeFormater(date).startOf('day').add(1, 'day').utc().format(),
                skip: skip,
                limit: LIMIT,
            };
            SCService.GetRebalancingEvents(
                params,
                () => startLoader('events'),
                handleEventsDataSuccess,
                handleError,
                () => stopLoader('events')
            );
        } else {
            setEvents([]);
        }
    }, [selectedGroup?.value, skip, refresh, date, selectedMode?.value]);

    const fetchSiteOverview = () => {
        const params = { groupId: selectedGroup.value };
        SCService.GetSiteOverview(params, startLoader, handleSiteOverviewSuccess, handleError, stopLoader);
    };

    const handleEventsDataSuccess = ({ data }) => {
        setEvents(
            _.get(data, ['data', 0, 'data'], []).map((e) => {
                const triggerAction = _.get(e, ['meta', 'triggerAction']);
                const triggerReason = `Session ${_.get(e, ['meta', 'triggeredBySessionId'])} was ${
                    triggerAction === 'ACTIVE' ? 'initiated' : triggerAction === 'COMPLETED' ? 'completed' : 'put to maintenance'
                }`;
                const activeSessions = _.get(e, ['meta', 'activeSessions'], []);
                return {
                    ...e,
                    timestamp: momentTimeFormater(e.createdAt).format('YYYY-MM-DD HH:mm:ss'),
                    triggerReason,
                    noOfActiveSessions: activeSessions.length,
                    maxConnectedCapacity: _.sumBy(activeSessions, (aS) => aS.connectorMaxCapacity),
                    totalAllocatedPower: _.sumBy(activeSessions, (aS) => aS.allocatedPower),
                    action: (
                        <div className={myClasses.ActionIcons}>
                            <div onClick={() => setInfoModal({ status: true, data: _.get(e, ['meta', 'activeSessions'], []) })}>
                                <CustomTooltip content={'Info'}>
                                    <UilNotes className={buttonClasses.ButtonIcon} />
                                </CustomTooltip>
                            </div>
                        </div>
                    ),
                };
            })
        );
        setTotalCount(_.get(data, ['data', 0, 'count', 0, 'count'], 0));
    };

    const handleSiteOverviewSuccess = ({ data }) => {
        if (data?.data) {
            setOverview(data.data);
        }
    };

    const handleError = (err) => {
        let data = err && err.response ? err.response.data : null;
        if (data) toast.error(data.message);
        else toast.error('Internal server error!');
    };

    const handlePageChange = (pageno) => {
        setSkip(LIMIT * pageno);
        setPage(pageno);
    };

    return (
        <>
            <div className={classes.Alert} style={{ marginBottom: '1vw' }}>
                <div className={SODClasses.CardsContainer}>
                    {['sitePowerLimit', 'currentDraw', 'activeSessions', 'lastRebalance'].map((parameter) => (
                        <div
                            className={SODClasses.Card}
                            style={parameter === 'currentDraw' && _.get(overview, ['currentDraw']) > _.get(overview, ['sitePowerLimit']) ? { backgroundColor: '#ffafaf' } : {}}
                        >
                            <div className={SODClasses.Value}>
                                {parameter === 'lastRebalance'
                                    ? moment().diff(moment().subtract(_.get(overview, [parameter]), 'seconds'), 'days') >= 1 ? '> 24hrs ago' : findDifferenceInHoursAndMinutesFormatted(moment().subtract(_.get(overview, [parameter]), 'seconds'), moment()) + ' ago'
                                    : `${_.get(overview, [parameter])} ${['sitePowerLimit', 'currentDraw'].includes(parameter) ? ' kW' : ''}`}
                            </div>
                            <div className={SODClasses.Parameter}>{CamelCaseToTextCase(parameter)}</div>
                        </div>
                    ))}
                </div>
            </div>
            <div className={classes.Alert}>
                <div className={classes.Header}>
                    <div>
                        <Typography content="Rebalance History" />
                        <div className={classes.TableCount}>
                            Total Count :
                            <span>
                                <Typography size="14" content={totalCount} />
                            </span>
                        </div>
                    </div>
                </div>
                <div>
                    <Table
                        head={['Timestamp (CET)', 'Status', 'Trigger', 'Active Sessions', 'Connector Capacity (kW)', 'Allocated Power (kW)', 'Action']}
                        keys={['timestamp', 'status', 'triggerReason', 'noOfActiveSessions', 'maxConnectedCapacity', 'totalAllocatedPower', 'action']}
                        data={events}
                        page={page}
                        Pagination={true}
                        limit={LIMIT}
                        handlePageChange={handlePageChange}
                        totalCount={totalCount}
                    />
                </div>
                <ModalComponent isOpen={infoModal.status} setOpen={() => setInfoModal({ status: false, data: [] })}>
                    <EventsTrasks setOpen={() => setInfoModal({ status: false, data: [] })} sessions={infoModal.data} />
                </ModalComponent>
            </div>
        </>
    );
};

export default LoadManagement;
